import { createTranslator, detectLanguage, type Language, type Translator } from '@fd/i18n';
import { useContext, useMemo, useState, type ReactNode } from 'react';
import { createStableContext } from './stable-context.ts';
import { readPreference, writePreference } from './config.ts';

type I18nContext = { t: Translator; language: Language; setLanguage(language: Language): void };

const Context = createStableContext<I18nContext | null>('i18n', null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = readPreference('language');
    return stored === 'pl' || stored === 'en' ? stored : detectLanguage(navigator.languages ?? [navigator.language]);
  });
  const value = useMemo<I18nContext>(
    () => ({
      t: createTranslator(language),
      language,
      setLanguage(next) {
        writePreference('language', next);
        document.documentElement.lang = next;
        setLanguageState(next);
      },
    }),
    [language],
  );
  document.documentElement.lang = language;
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useI18n(): I18nContext {
  const context = useContext(Context);
  if (!context) throw new Error('useI18n outside I18nProvider');
  return context;
}

export const useT = (): Translator => useI18n().t;
